import { Link } from "react-router-dom";
import { ExternalLink, Star, ArrowRight, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import { LOCATION_KEYWORDS } from "@/components/SEO";

const backlinks = [
  {
    title: "C. Glancey Windows & Joiners — Tradesmen Up",
    url: "https://www.tradesmenup.co.uk/75008/c-glancey-windows-joiners",
    description: "View our verified profile on Tradesmen Up — one of the UK's leading directories for trusted tradesmen. See our credentials, services, and customer feedback.",
  },
  {
    title: "C. Glancey — MyBuilder Reviews",
    url: "https://www.mybuilder.com/profile/cgbuildingjoiner/reviews",
    description: "Read independent customer reviews of our work on MyBuilder. See real feedback from homeowners across Lanarkshire who have used our double glazing and joinery services.",
  },
  {
    title: "C. Glancey Windows & Joiners — Double Glazing Windows Directory",
    url: "https://double-glazing-windows.co.uk/listing/c-glancey-windows-and-joiners/",
    description: "Our listing on the Double Glazing Windows directory — a specialist directory for double glazing companies across the UK.",
  },
  {
    title: "C. Glancey — Pinterest",
    url: "https://uk.pinterest.com/charlesglancey/",
    description: "Browse our Pinterest boards for inspiration on double glazing, composite doors, garden rooms, and home improvement ideas.",
  },
];

const BacklinksPage = () => {
  return (
    <div data-testid="backlinks-page">
      <SEO
        title="Find Us Online | Double Glazing Lanarkshire | C. Glancey"
        description="Find C. Glancey Double Glazing on trusted directories and review sites. Read independent reviews of our double glazing, uPVC windows, and composite doors in Lanarkshire, Motherwell, Hamilton & Glasgow."
        keywords={`C Glancey reviews, double glazing reviews Lanarkshire, trusted trader, MyBuilder reviews, double glazing directory, window fitters near me, ${LOCATION_KEYWORDS}`}
        canonical="/find-us"
      />

      {/* Hero */}
      <section className="relative pt-44 pb-20 bg-[#0A0A0A]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF6B00]/10 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
            data-testid="backlinks-h1"
          >
            FIND US <span className="text-[#FF6B00]">ONLINE</span>
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Find C. Glancey Double Glazing on trusted trade directories and review platforms. Read independent reviews of our <Link to="/windows" className="text-[#FF6B00] hover:underline">double glazing</Link>, <Link to="/doors" className="text-[#FF6B00] hover:underline">composite doors</Link>, and <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link> from homeowners across Lanarkshire.
          </p>
        </div>
      </section>

      {/* Links */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-8"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            TRUSTED DIRECTORIES & <span className="text-[#FF6B00]">REVIEWS</span>
          </h2>
          <div className="space-y-6">
            {backlinks.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`backlink-${i}`}
                className="block bg-[#F5F5F5] p-6 border-l-4 border-[#FF6B00] hover:bg-[#FFF5EE] transition-colors group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-[#0A0A0A] mb-2 group-hover:text-[#FF6B00] transition-colors">
                      {link.title}
                    </h3>
                    <p className="text-[#525252] text-sm mb-2">{link.description}</p>
                    <span className="text-[#FF6B00] text-sm font-medium">{link.url}</span>
                  </div>
                  <ExternalLink className="w-5 h-5 text-[#FF6B00] flex-shrink-0 mt-1" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trust Us */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-white mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            WHY CHOOSE <span className="text-[#FF6B00]">C. GLANCEY</span> DOUBLE GLAZING?
          </h2>
          <p className="text-gray-300 leading-relaxed mb-6">
            We are local double glazing installers in Lanarkshire with a proven track record of quality workmanship and customer satisfaction. Our profiles on independent trade directories and review sites speak for themselves — real reviews from real homeowners who have trusted us with their high-quality uPVC windows, composite doors, and affordable replacement windows.
          </p>
          <p className="text-gray-300 leading-relaxed mb-6">
            As one of the most trusted local window companies in the area, we pride ourselves on honest pricing, professional installation, and outstanding aftercare. Every installation comes with a 10 year guarantee. We serve <Link to="/double-glazing-hamilton" className="text-[#FF6B00] hover:underline">Hamilton</Link>, <Link to="/double-glazing-motherwell" className="text-[#FF6B00] hover:underline">Motherwell</Link>, <Link to="/double-glazing-east-kilbride" className="text-[#FF6B00] hover:underline">East Kilbride</Link>, <Link to="/double-glazing-airdrie" className="text-[#FF6B00] hover:underline">Airdrie</Link>, <Link to="/double-glazing-coatbridge" className="text-[#FF6B00] hover:underline">Coatbridge</Link>, <Link to="/double-glazing-wishaw" className="text-[#FF6B00] hover:underline">Wishaw</Link>, Glasgow, and across Lanarkshire.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Don't just take our word for it — read our <Link to="/reviews" className="text-[#FF6B00] hover:underline">customer reviews</Link>, browse our <Link to="/recent-work" className="text-[#FF6B00] hover:underline">recent work gallery</Link>, or visit any of the independent sites listed above to see what our customers say about us.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#FF6B00]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl sm:text-3xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            GET A FREE QUOTE TODAY
          </h2>
          <p className="text-white/90 mb-8">
            Ready to upgrade your home with double glazing, <Link to="/windows" className="text-white underline hover:text-[#0A0A0A]">replacement windows</Link>, or <Link to="/doors" className="text-white underline hover:text-[#0A0A0A]">composite doors</Link>? Call us or request a free, no-obligation quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:07917181597"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-all"
            >
              <Phone className="w-5 h-5" />
              Call 07917 181597
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all"
            >
              Request Free Quote <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Explore Our Services</h3>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/windows" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Double Glazing Windows
            </Link>
            <Link to="/doors" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Composite Doors
            </Link>
            <Link to="/outbuilds" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Garden Rooms
            </Link>
            <Link to="/reviews" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Customer Reviews
            </Link>
            <Link to="/recent-work" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Recent Work
            </Link>
            <Link to="/contact" className="px-5 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BacklinksPage;
